import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Textarea } from '../../../components/ui/Textarea';
import { ballotGuideAPI } from '../api/ballot-guide';

/**
 * Ballot Guide Delivery CTA & Bottom Sheet
 * Sticky footer CTA: "Take this with me"
 * Expands to show print/email/mail options with customization
 */
function BallotGuideDelivery({ guideId, _electionName, recipientEmail }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('delivery'); // 'delivery', 'customize', 'confirm'
  const [deliveryMethod, setDeliveryMethod] = useState('print'); // print, email, mail
  const [options, setOptions] = useState({
    includeSelections: true,
    includeSummaries: false,
    includePollingInfo: true,
    largePrint: false,
    blackAndWhiteFriendly: false,
  });
  const [mailingInfo, setMailingInfo] = useState({
    name: '',
    address: '',
  });

  const deliveryMutation = useMutation({
    mutationFn: (data) => ballotGuideAPI.requestDelivery(guideId, data),
    onSuccess: () => {
      setStep('confirm');
    },
  });

  const handleDelivery = async () => {
    const payload = {
      deliveryMethod,
      ...(deliveryMethod === 'email' && { recipientEmail }),
      ...(deliveryMethod === 'mail' && {
        recipientName: mailingInfo.name,
        mailingAddress: mailingInfo.address,
      }),
      ...options,
    };

    deliveryMutation.mutate(payload);
  };

  const handleClose = () => {
    if (step === 'confirm') {
      setIsOpen(false);
      setStep('delivery');
    } else {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Sticky CTA Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 p-4 shadow-lg lg:sticky lg:mt-6">
        <Button
          onClick={() => setIsOpen(true)}
          className="w-full lg:w-auto flex items-center justify-center gap-2"
        >
          <span>📋</span>
          Take this with me
        </Button>
      </div>

      {/* Dialog Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Your voting guide</DialogTitle>
          </DialogHeader>

          {/* Content */}
          <div>
            {step === 'delivery' && (
              <>
                <p className="text-neutral-600 text-lg mb-6">
                  Bring a simple reference to the polls
                </p>

                {/* Delivery Method Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  {/* Print Now */}
                  <button
                    onClick={() => {
                      setDeliveryMethod('print');
                      setStep('customize');
                    }}
                    className={`p-4 rounded-lg border-2 transition-all text-center ${
                      deliveryMethod === 'print'
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <div className="text-4xl mb-2">🖨️</div>
                    <h3 className="font-bold text-neutral-900">Print now</h3>
                    <p className="text-sm text-neutral-600">Get it instantly</p>
                  </button>

                  {/* Email PDF */}
                  <button
                    onClick={() => {
                      setDeliveryMethod('email');
                      setStep('customize');
                    }}
                    className={`p-4 rounded-lg border-2 transition-all text-center ${
                      deliveryMethod === 'email'
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <div className="text-4xl mb-2">📧</div>
                    <h3 className="font-bold text-neutral-900">Email me PDF</h3>
                    <p className="text-sm text-neutral-600">Instant delivery</p>
                  </button>

                  {/* Mail Printed */}
                  <button
                    onClick={() => {
                      setDeliveryMethod('mail');
                      setStep('customize');
                    }}
                    className={`p-4 rounded-lg border-2 transition-all text-center ${
                      deliveryMethod === 'mail'
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <div className="text-4xl mb-2">📮</div>
                    <h3 className="font-bold text-neutral-900">Mail me printed</h3>
                    <p className="text-sm text-neutral-600">5–7 days</p>
                  </button>
                </div>
              </>
            )}

            {step === 'customize' && (
              <>
                <h3 className="text-xl font-bold text-neutral-900 mb-6">Customize your guide</h3>

                {/* Options */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="includeSelections"
                      checked={options.includeSelections}
                      onCheckedChange={(checked) =>
                        setOptions({ ...options, includeSelections: checked })
                      }
                    />
                    <Label htmlFor="includeSelections" className="cursor-pointer">
                      Include my selected choices
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="includeSummaries"
                      checked={options.includeSummaries}
                      onCheckedChange={(checked) =>
                        setOptions({ ...options, includeSummaries: checked })
                      }
                    />
                    <Label htmlFor="includeSummaries" className="cursor-pointer">
                      Include candidate summaries
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="includePollingInfo"
                      checked={options.includePollingInfo}
                      onCheckedChange={(checked) =>
                        setOptions({
                          ...options,
                          includePollingInfo: checked,
                        })
                      }
                    />
                    <Label htmlFor="includePollingInfo" className="cursor-pointer">
                      Include polling location info
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="largePrint"
                      checked={options.largePrint}
                      onCheckedChange={(checked) => setOptions({ ...options, largePrint: checked })}
                    />
                    <Label htmlFor="largePrint" className="cursor-pointer">
                      Large print
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="blackAndWhiteFriendly"
                      checked={options.blackAndWhiteFriendly}
                      onCheckedChange={(checked) =>
                        setOptions({
                          ...options,
                          blackAndWhiteFriendly: checked,
                        })
                      }
                    />
                    <Label htmlFor="blackAndWhiteFriendly" className="cursor-pointer">
                      Black & white friendly
                    </Label>
                  </div>
                </div>

                {/* Mailing Address for Mail Delivery */}
                {deliveryMethod === 'mail' && (
                  <div className="bg-muted p-4 rounded-lg mb-6 space-y-4">
                    <h4 className="font-bold text-foreground">Where should we mail it?</h4>
                    <div>
                      <Label htmlFor="mailingName">Your name</Label>
                      <Input
                        id="mailingName"
                        placeholder="Full name"
                        value={mailingInfo.name}
                        onChange={(e) => setMailingInfo({ ...mailingInfo, name: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mailingAddress">Mailing address</Label>
                      <Textarea
                        id="mailingAddress"
                        placeholder="Street address, city, state, zip"
                        value={mailingInfo.address}
                        onChange={(e) =>
                          setMailingInfo({ ...mailingInfo, address: e.target.value })
                        }
                        rows="3"
                        className="mt-2"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Request by Oct 20 to receive before Election Day
                      <br />
                      Usually arrives in 5–7 days
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button onClick={() => setStep('delivery')} variant="outline" className="flex-1">
                    Back
                  </Button>
                  <Button
                    onClick={handleDelivery}
                    disabled={
                      deliveryMutation.isPending ||
                      (deliveryMethod === 'mail' && (!mailingInfo.name || !mailingInfo.address))
                    }
                    className="flex-1"
                  >
                    {deliveryMutation.isPending ? 'Sending...' : 'Send'}
                  </Button>
                </div>
              </>
            )}

            {step === 'confirm' && (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">✓</div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-2">
                  {deliveryMethod === 'print' && 'Ready to print'}
                  {deliveryMethod === 'email' && 'Guide emailed'}
                  {deliveryMethod === 'mail' && 'Guide on the way'}
                </h3>
                <p className="text-neutral-600 mb-6">
                  {deliveryMethod === 'print' &&
                    'Click the printer icon above to print your guide.'}
                  {deliveryMethod === 'email' && 'Check your email for your voting guide PDF.'}
                  {deliveryMethod === 'mail' &&
                    'Your printed guide will arrive in 5–7 business days.'}
                </p>
                <Button onClick={handleClose} className="w-full">
                  Done
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

BallotGuideDelivery.propTypes = {
  guideId: PropTypes.string.isRequired,
  _electionName: PropTypes.string.isRequired,
  recipientEmail: PropTypes.string.isRequired,
};

export default BallotGuideDelivery;
